/**
 * Admission QnA Data Module
 * API functions for QnA sessions and messages
 */

import { api } from '@/Data/api';
import ss from '@/Data/Session';
import type { ApiResponse, ApiListResponse } from 'ai-api/types/common';
import type {
    QnaSession,
    QnaMessage,
    ListQnaSessionsParams,
    GetQnaMessagesParams,
    CreateQnaSessionParams,
    UpdateQnaSessionParams,
    DeleteQnaSessionParams,
} from 'ai-api/types/admission-qna';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` } });

// ============ Session Functions ============

export async function listSessions(params?: ListQnaSessionsParams): Promise<QnaSession[] | null> {
    try {
        const res = await api.get<ApiListResponse<QnaSession>>('/admission/qna/session', { params, ...authHeaders() });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to list QnA sessions:', e);
        return null;
    }
}

export async function createSession(params?: CreateQnaSessionParams): Promise<QnaSession | null> {
    try {
        const res = await api.post<ApiResponse<QnaSession>>('/admission/qna/session', params ?? {}, authHeaders());
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to create QnA session:', e);
        return null;
    }
}

export async function updateSession(params: UpdateQnaSessionParams): Promise<QnaSession | null> {
    try {
        const res = await api.patch<ApiResponse<QnaSession>>('/admission/qna/session', params, authHeaders());
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to update QnA session:', e);
        return null;
    }
}

export async function deleteSession(params: DeleteQnaSessionParams): Promise<boolean> {
    try {
        const res = await api.delete<ApiResponse<void>>('/admission/qna/session', { params, ...authHeaders() });
        return res.data?.code === 200;
    } catch (e) {
        console.error('Failed to delete QnA session:', e);
        return false;
    }
}

// ============ Message Functions ============

export async function listMessages(params: GetQnaMessagesParams): Promise<QnaMessage[] | null> {
    try {
        const res = await api.get<ApiListResponse<QnaMessage>>('/admission/qna/message', { params, ...authHeaders() });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to list QnA messages:', e);
        return null;
    }
}

// Re-export types
export type {
    QnaSession,
    QnaMessage,
    ListQnaSessionsParams,
    GetQnaMessagesParams,
    CreateQnaSessionParams,
    UpdateQnaSessionParams,
    DeleteQnaSessionParams,
    QnaAskParams,
    QnaSSEEvent,
} from 'ai-api/types/admission-qna';

// Export stream client
export { QnAStreamClient } from './Stream';
export type { QnAStreamCallbacks } from './Stream';
